import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/api/client';
import { fetchVotes, postVote, type VotePayload, type VotesResponse } from '@/api/plan';
import { tripKeys } from '@/api/queryKeys';
import type { VotesMap } from '@/api/types';

/**
 * Liefert die globale Votes-Map (actId → Voter-Liste) plus eine Mutation
 * `toggleVote(actId, vote, name)` mit optimistischem Update + Rollback bei Fehler.
 *
 * Vote-Identität: bevorzugt per Session-Token (Backend liest aus Cookie), für
 * Anonyme via `name`. Der UI-Check `hasVoted(votes, actId, identity)` weiter unten
 * kapselt diese Logik einheitlich.
 */
export function useVotes(slug: string) {
  const qc = useQueryClient();

  const votesQuery = useQuery({
    queryKey: tripKeys.votes(slug),
    queryFn: ({ signal }) => fetchVotes(slug, signal).then((r) => r.votes),
    staleTime: 30_000,
  });

  const voteMutation = useMutation<VotesResponse, ApiError, VotePayload & { ownVoterKey: string }>({
    mutationFn: (input) => postVote(slug, { actId: input.actId, vote: input.vote, name: input.name }),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: tripKeys.votes(slug) });
      const previous = qc.getQueryData<VotesMap>(tripKeys.votes(slug));
      // Optimistische Patch-Logik: bei `vote=true` füge {token?, name} hinzu, bei
      // `vote=false` entferne den eigenen Eintrag. Identifikation läuft über
      // einen vom Caller mitgegebenen Schlüssel — siehe `voterKey(identity)`.
      qc.setQueryData<VotesMap>(tripKeys.votes(slug), (current) => {
        const next: VotesMap = { ...(current ?? {}) };
        const list = [...(next[input.actId] ?? [])];
        const idx = list.findIndex((v) => voterKey(v) === input.ownVoterKey);
        if (input.vote) {
          if (idx === -1 && input.name) list.push({ name: input.name });
        } else if (idx !== -1) {
          list.splice(idx, 1);
        }
        next[input.actId] = list;
        return next;
      });
      return { previous };
    },
    onError: (_err, _input, context) => {
      // Rollback: alten Snapshot wiederherstellen, damit das UI nicht in einem
      // inkonsistenten Zwischenzustand stecken bleibt.
      const ctx = context as { previous?: VotesMap } | undefined;
      if (ctx?.previous) qc.setQueryData(tripKeys.votes(slug), ctx.previous);
    },
    onSuccess: (data) => {
      // Server liefert die authoritative Map zurück — diese überschreibt unseren Optimist-Patch.
      qc.setQueryData<VotesMap>(tripKeys.votes(slug), data.votes);
    },
  });

  return {
    votes: votesQuery.data ?? {},
    isLoading: votesQuery.isLoading,
    isError: votesQuery.isError,
    voteMutation,
  };
}

/**
 * Identifier eines Voters für Vergleichs- und Dedup-Zwecke. Token gewinnt
 * gegen Namen (zwei User können denselben Namen haben), Namen-only Voter
 * (legacy) bekommen den Namen als Schlüssel.
 */
export function voterKey(voter: { token?: string; name?: string }): string {
  if (voter.token) return `t:${voter.token}`;
  return `n:${(voter.name ?? '').toLowerCase()}`;
}

/** Hat der eingeloggte User für `actId` schon gevotet? */
export function hasVoted(votes: VotesMap, actId: string, identity: { token?: string; name?: string }): boolean {
  const list = votes[actId] ?? [];
  const myKey = voterKey(identity);
  return list.some((v) => voterKey(v) === myKey);
}
